import { ClientOutputs } from '@botpress/client'
import { QuotaType } from '@bpinternal/const'
import { DateTime } from 'luxon'
import Stripe from 'stripe'
import { ADDONS_QUOTA_TYPES } from '../hooks/useAddons'

type WorkspaceUsageStatus = 'OK' | 'UsageLimitReached' | 'UsageWarning'

export type Usage = {
  quantity: number
  price: number
  minimum: number
  maximum: number
}

export type WorkspaceUsages = {
  status: WorkspaceUsageStatus
}

export type WorkspaceBillingPeriodDates = { start: DateTime; end: DateTime }

export type ProductWithPrices = Stripe.Product & { price: Stripe.Price; prices: Stripe.Price[] }

export type ProductQuantityMap = Record<
  string,
  {
    quantity: number
    priceId: string
  }
>

export type AddonProductMap = Record<
  string,
  ProductQuantityMap[string] & {
    productDetails: ProductWithPrices
    name: string
    pricePerMonth: Stripe.Price
    quotaName: string
  }
>
export type PlanProductMap = Record<
  string,
  ProductQuantityMap[string] & {
    productId: string
    price: {
      perMonth: Stripe.Price
      perYear: Stripe.Price
    }
  }
>

export type PlanProduct = {
  id: string
  cycle: 'yearly' | 'monthly' | undefined
  legacyName: Plan
  priceId: string
  pricePerMonth: number
  cancelAtPeriodEnd?: boolean
}

export type PlanInfoMap = Record<Plan, PlanInfo>

export type PlanInfo = {
  productId: string
  legacyName: Plan
  displayName: string
  backgroundUrl?: string
  features: string[]
  addons?: {
    quotaType: QuotaType
    display: string
    value: number
  }[]
  description: string
  priceSuffix: string
  previousPlan?: Plan
}

export type AddonQuotaType = (typeof ADDONS_QUOTA_TYPES)[number]

export const AUTO_RECHARGE_SUPPORTED_ADDON_TYPES = [
  'table_row_count',
  'invocation_calls',
  'knowledgebase_vector_storage',
  'storage_count',
] as const

type AutoRechargeSetting =
  | { enabled: true; threshold: number; limit: number; disabled_reason?: never }
  | { enabled: false; disabled_reason: string; threshold?: never; limit?: never }

export type AutoRechargeSettings = Record<(typeof AUTO_RECHARGE_SUPPORTED_ADDON_TYPES)[number], AutoRechargeSetting>

export type AddonDetails = {
  productId: string
  currentQuantity: number
  nextPaymentQuantity: number
  priceId: string
  productDetails: ProductWithPrices
  name: string
  displayName: string
  priceCents: number
  quotaName: AddonQuotaType
  description: string
  autoRecharge:
    | { supported: false; settings?: never }
    | { supported: true; settings: AutoRechargeSettings[keyof AutoRechargeSettings] }
}

export type BillingVersion = ClientOutputs['getWorkspace']['billingVersion']

export type Invoice = {
  amount: number
  currency: string
  link: string
  paymentStatus: 'paid' | 'unpaid'
  period: {
    month: number
    year: number
  }
}

export type PromoCode = {
  code: string
  coupon: {
    percentOff: number
    durationInMonths: number
    appliesTo: string[]
  }
}

export type BillingInfo = {
  billingReadonly: boolean
}

export type ProductQuantityConfig = Array<{
  price: string
  quantity?: number
}>

export type Plan = ClientOutputs['getWorkspace']['plan']

export type TrialPlan = 'team' | 'plus'

export type Trial = {
  id: string
  workspaceId: string
  trialPlan: Plan | undefined
  fromPlan: Plan
  endsAt: DateTime
  isActive: boolean
}
